import { ChatBubbleOvalLeftIcon, HeartIcon } from "@heroicons/react/24/outline";
import { HeartIcon as HeartIconSolid } from "@heroicons/react/24/solid";
import api from "@src/api/api";
import { formatDateDifference } from "@src/components/elements/PostWidget";
import UserWidget from "@src/components/elements/UserWidget";
import CreatePostComment from "@src/components/elements/posts/comments/CreatePostComment";
import TextView from "@src/components/elements/textedit/TextView";
import SharePopup from "@src/components/popup/SharePopup";
import CommentsDropdown from "@src/pages/news/CommentsDropdown";
import PostsActionBarItem from "@src/pages/news/posts/PostsActionBarItem";
import { useAppSelector } from "@src/state/hooks";
import useLoginModal from "@src/state/modal/useLoginModal";
import getName from "@src/utils/getName";
import { CommentType } from "@src/utils/types/CommentType";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import tw from "twin.macro";

type Props = {
  level?: number;
  comment: CommentType;
  comments: CommentType[];
  viewCount?: number;
  postId: string;
  key?: string;
  parentId?: string;
};
const PostComment = ({ comment, ...props }: Props) => {
  const [answer, setAnswer] = useState<boolean>(false);
  const [sharePopupOpen, setSharePopupOpen] = useState<boolean>(false);
  const [showReplies, setShowReplies] = useState<boolean>(false);
  const { user } = useAppSelector(state => state.user);
  const loginModal = useLoginModal();
  const [likeEffect, setLikeEffect] = useState(false);
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const like = useMutation([`like-${comment.id}-${user ? user.id : ""}`], {
    mutationFn: async () => {
      const res = await api.get(`/api/v1/comments/${comment.id}/like`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comments"] });
    },
    onError: () => {
      alert("Failed");
    },
  });

  const unlike = useMutation([`unlike-${comment.id}-${user ? user.id : ""}`], {
    mutationFn: async () => {
      const res = await api.get(`/api/v1/comments/${comment.id}/unlike`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comments"] });
    },
    onError: () => {
      alert("Failed");
    },
  });

  return (
    <>
      <div className="group/comment relative flex gap-3">
        <UserWidget user={comment.author} size={30} />
        <div className="w-[calc(100%-42px)]">
          <div className={"rounded-xl border-2 border-lightgray bg-lightgray px-1.5 py-1"}>
            <div className="flex w-full gap-1">
              <div
                className="truncate text-sm font-semibold text-lightblue"
                style={{ maxWidth: "calc(100% - 150px)" }}
              >
                {getName(comment.author)}
              </div>
              <div className="shrink-0 text-xs leading-5 text-gray-600">
                - {formatDateDifference(new Date(), new Date(comment.created_at))}
              </div>
              <div className="ml-auto">
                <CommentsDropdown comment={comment} />
              </div>
            </div>
            <div className="whitespace-pre-wrap break-all text-xs">
              <TextView value={comment.text} disableTopMargin />
            </div>

            <div className="relative mt-1 flex flex-row justify-between gap-4 text-darkblue">
              <PostsActionBarItem
                onClick={() => {
                  user ? setAnswer(value => !value) : loginModal.open();
                }}
                label={t("components.posts.answer")}
                count={comment.comment_count}
              >
                <ChatBubbleOvalLeftIcon />
              </PostsActionBarItem>
              <PostsActionBarItem
                label={t("main.contacts.tabs.likes")}
                count={comment.like_count}
                onClick={() => (comment.liked ? unlike.mutate() : like.mutate())}
                textColor={
                  comment.liked
                    ? tw`text-red-600`
                    : tw`text-gray-600 transition-all ease-out duration-200 group-hover:text-red-600`
                }
                color={comment.liked ? tw`bg-pink-100` : tw`group-hover:bg-pink-100 `}
              >
                {comment.liked ? (
                  <HeartIconSolid className={`${likeEffect && "animate-ping-once"}`} />
                ) : (
                  <HeartIcon
                    onClick={() => {
                      setLikeEffect(true);
                    }}
                    onAnimationEnd={() => {
                      setLikeEffect(false);
                    }}
                  />
                )}
              </PostsActionBarItem>

              {sharePopupOpen && (
                <SharePopup
                  postId={props.postId}
                  userId=""
                  content={{
                    title: "News",
                    body: "https://www.tradefoox.com/news/" + props.postId,
                  }}
                  onClose={() => setSharePopupOpen(false)}
                />
              )}
            </div>
          </div>
          {!showReplies && comment.comment_count !== 0 && (
            <div className="mt-1 flex cursor-pointer text-sm" onClick={() => setShowReplies(true)}>
              <ChatBubbleOvalLeftIcon className="size-5 px-1" />
              <span>
                {comment.comment_count} {t("components.posts.answer")}
              </span>
            </div>
          )}
        </div>
      </div>
      {answer && (
        <CreatePostComment
          parentId={props.level && props.level > 1 ? props.parentId : comment.id}
          answer={comment}
          postId={props.postId}
          onSuccess={() => setAnswer(false)}
        />
      )}
      {showReplies && (
        <div className="grid grid-cols-1 gap-3 pl-10">
          {props.comments.map(
            child =>
              child.parent_id === comment.id && (
                <PostComment
                  viewCount={props.viewCount}
                  comments={props.comments}
                  level={props.level != undefined ? props.level + 1 : undefined}
                  postId={props.postId}
                  key={child.id}
                  parentId={comment.id}
                  comment={child}
                />
              ),
          )}
        </div>
      )}
    </>
  );
};

export default PostComment;
